import { put, race, select, take } from 'redux-saga/effects';

import { type GeneratorResult, runSagaAsPromise, type SagaResult } from '@pressreader/appstore';
import {
    authenticationDone,
    authenticationRequestAction,
    loginAction,
    loginDone,
    loginFailed,
    logOutDone,
    logOutFailed,
    logOutRequestAction,
    pwdRecoveryDone,
    pwdRecoveryFailed,
    recoverPwdAction,
    selectUser,
    signUpAction,
    signUpDone,
    signUpFailed,
    userChangeDone,
    userChangeFailed,
} from '@pressreader/authentication-core';
import { type AnySignInWithSystem, type AnySignUpAsSystemData, type ILoginFlowContext, SignInDialogVersion } from '@pressreader/authentication-types';
import { openAlertOk } from '@pressreader/popups-alert';
import { localizedString } from '@pressreader/resources';

import { SignupStatus } from '../index';

function* awaitLoginCompletion(): GeneratorResult<boolean> {
    const result: { done: unknown; failed: unknown } = yield race({
        done: take(loginDone),
        failed: take(loginFailed),
    });

    if (result.done) {
        yield take(userChangeDone);
    } else {
        yield take(userChangeFailed);
    }

    return !!result.done;
}

function* requestLoginSaga(loginAs: AnySignInWithSystem): GeneratorResult<boolean> {
    yield put(loginAction({ loginAs }));
    return yield* awaitLoginCompletion();
}

function* logInSaga(loginAs: AnySignInWithSystem): GeneratorResult<boolean> {
    const user: ReturnType<typeof selectUser> = yield select(selectUser);

    if (user.isAuthenticated) {
        return true;
    }

    return yield* requestLoginSaga(loginAs);
}

function* logInWithoutAuthenticationCheckSaga(loginAs: AnySignInWithSystem): GeneratorResult<boolean> {
    return yield* requestLoginSaga(loginAs);
}

function* logOutSaga(): GeneratorResult<boolean> {
    yield put(logOutRequestAction());

    const result: { done: unknown; failed: unknown } = yield race({
        done: take(logOutDone),
        failed: take(logOutFailed),
    });

    if (result.done) {
        yield take(userChangeDone);
    } else {
        yield take(userChangeFailed);
    }

    return !!result.done;
}

function* awaitSignUpCompletion(): GeneratorResult<SignupStatus> {
    const result: { done: ReturnType<typeof signUpDone>; failed: unknown } = yield race({
        done: take(signUpDone),
        failed: take(signUpFailed),
    });

    if (result.failed) {
        yield take(userChangeFailed);
        return SignupStatus.Error;
    }

    yield take(userChangeDone);
    const { emailValidationRequired, email } = result.done.payload;

    if (emailValidationRequired) {
        if (email) {
            return SignupStatus.PendingVerification;
        } else {
            yield openAlertOk({ message: localizedString('Messages.Dialog.EmailSent'), isHtml: true });
        }
    }
    return SignupStatus.Success;
}

function* signUpSaga(signUpAs: AnySignUpAsSystemData): GeneratorResult<SignupStatus> {
    yield put(signUpAction({ signUpAs }));
    return yield* awaitSignUpCompletion();
}

function* recoverPasswordSaga(recoveryData: { email: string }): GeneratorResult<boolean> {
    const { email } = recoveryData;

    yield put(recoverPwdAction({ email }));

    const result: { done: unknown; failed: unknown } = yield race({
        done: take(pwdRecoveryDone),
        failed: take(pwdRecoveryFailed),
    });

    return !!result.done;
}

const defaultLoginFlowParameters: ILoginFlowContext = {
    login: true,
    signUp: false,
};

type DialogOptions = {
    dialogVersion: SignInDialogVersion;
    introTextResourceId?: string;
    disableClose?: true;
};

function* loginFlowSaga(
    dialogOptions: DialogOptions = { dialogVersion: SignInDialogVersion.V1 },
    startWith: ILoginFlowContext = defaultLoginFlowParameters,
): SagaResult<boolean> {
    const user: ReturnType<typeof selectUser> = yield select(selectUser);
    if (user.isAuthenticated) {
        return true;
    }

    const { dialogVersion, introTextResourceId, disableClose } = dialogOptions;
    yield put(authenticationRequestAction({ dialogVersion, introTextResourceId, startWith, disableClose }));

    const action: ReturnType<typeof authenticationDone> = yield take(authenticationDone);
    return action.payload.authenticationResult;
}

/**
 * Promise facade over `loginFlowSaga` for saga-free callers (listener effects, async code): runs the same
 * saga via `runSagaAsPromise`, so the flow's action contract and ordering guarantees are preserved
 * verbatim. Resolves with the authentication result; rejects if the flow saga errors.
 */
function loginFlowAsync(dialogOptions?: DialogOptions, startWith?: ILoginFlowContext): Promise<boolean> {
    return runSagaAsPromise(() => loginFlowSaga(dialogOptions, startWith));
}

export {
    awaitLoginCompletion,
    awaitSignUpCompletion,
    defaultLoginFlowParameters,
    type DialogOptions,
    loginFlowAsync,
    loginFlowSaga,
    logInSaga,
    logInWithoutAuthenticationCheckSaga,
    logOutSaga,
    recoverPasswordSaga,
    signUpSaga,
};
